import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#experience", label: "Notre expérience" },
    { href: "#pourquoi-nous", label: "Pourquoi nous" },
    { href: "#a-propos", label: "À propos" },
    { href: "/blog", label: "Blog", isPage: true },
    { href: "#contact", label: "Contact" },
  ];

  const handleNavClick = (e, link) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    if (link.isPage) {
      navigate(link.href);
    } else {
      // If we're not on home page, navigate to home first
      if (location.pathname !== "/") {
        navigate("/" + link.href);
      } else {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  };

  const handleCtaClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/#contact");
    } else {
      const element = document.querySelector("#contact");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "header-blur border-b border-gray-100 shadow-sm"
          : "bg-white"
      }`}
      role="banner"
    >
      <nav
        className="max-w-7xl mx-auto px-6 md:px-12"
        role="navigation"
        aria-label="Navigation principale"
      >
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center hover:opacity-80 transition-opacity"
            data-testid="logo-link"
            aria-label="Performance Transmission - Accueil"
          >
            <img 
              src="/logo.svg" 
              alt="Performance Transmission" 
              className="h-10 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link)}
                className="text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium"
                data-testid={`nav-${link.href.replace("#", "").replace("/", "")}`}
              >
                {link.label}
              </a>
            ))}
            <Button
              onClick={handleCtaClick}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 btn-primary"
              data-testid="cta-header-btn"
            >
              Prendre rendez-vous
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-blue-600 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            id="mobile-menu"
            className="md:hidden py-4 border-t border-gray-100 mobile-menu-enter"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link)}
                  className="text-gray-600 hover:text-blue-600 transition-colors py-2 font-medium"
                  data-testid={`mobile-nav-${link.href.replace("#", "").replace("/", "")}`}
                >
                  {link.label}
                </a>
              ))}
              <Button
                onClick={handleCtaClick}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full mt-2 btn-primary"
                data-testid="mobile-cta-btn"
              >
                Prendre rendez-vous
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
